// Awin-Advertiser-Tab: die Aufbau-Checkliste bis zum Programmstart, Schritt für Schritt
// abhakbar. Inhalte kommen aus om_awin (befüllt von awin_seed.mjs), hier wird nur gerendert
// und der Status umgeschaltet.
//
// Bewusst dieselbe Mechanik wie der Masterplan-Tab: ein Klick schaltet offen → läuft →
// erledigt → offen. Wer das eine bedienen kann, kann auch das andere.
//
// Unterschied zum Masterplan: Die Phasen sind CHRONOLOGISCH gemeint, nicht nach Wichtigkeit.
// Phase A blockiert alles Weitere — deshalb bekommt sie einen eigenen Hinweis, solange sie
// nicht abgearbeitet ist. Ohne den läge die Verlockung nahe, mit den hübschen Aufgaben aus
// Phase D anzufangen, während die Antworten von Awin, Steuerberater und Frank noch fehlen.
import type { MarketingDeps } from "./marketing.js";

interface Schritt {
  id: number; kennung: string; phase: string; phase_titel: string;
  titel: string; detail: string | null; aufwand: string | null; hebel: number;
  status: "offen" | "laeuft" | "erledigt" | "verworfen"; notiz: string | null; erledigt_am: string | null;
}
interface AwinAntwort { schritte: Schritt[]; summe: { gesamt: number; erledigt: number; laeuft: number } }

const NAECHSTER: Record<string, Schritt["status"]> = {
  offen: "laeuft", laeuft: "erledigt", erledigt: "offen", verworfen: "offen",
};
const ZEICHEN: Record<string, string> = { offen: "○", laeuft: "◐", erledigt: "✓", verworfen: "—" };
const AUFWAND_TEXT: Record<string, string> = { S: "kleiner Aufwand", M: "mittlerer Aufwand", L: "großer Aufwand" };

function balken(erledigt: number, gesamt: number): HTMLElement {
  const wrap = document.createElement("div");
  wrap.className = "mp-fortschritt";
  const anteil = gesamt ? Math.round((erledigt / gesamt) * 100) : 0;
  const zahl = document.createElement("div");
  zahl.className = "mp-fortschritt__zahl";
  zahl.textContent = `${erledigt} von ${gesamt} Schritten · ${anteil} %`;
  const schiene = document.createElement("div");
  schiene.className = "mp-fortschritt__schiene";
  const fuell = document.createElement("div");
  fuell.className = "mp-fortschritt__fuellung";
  fuell.style.width = `${anteil}%`;
  schiene.appendChild(fuell);
  wrap.append(zahl, schiene);
  return wrap;
}

function zeile(s: Schritt, umschalten: (s: Schritt) => void): HTMLElement {
  const row = document.createElement("div");
  row.className = `mp-schritt mp-schritt--${s.status}`;
  row.tabIndex = 0;
  row.setAttribute("role", "button");
  row.setAttribute("aria-label", `${s.kennung} ${s.titel} — Status ${s.status}, klicken zum Weiterschalten`);

  const marke = document.createElement("span");
  marke.className = "mp-schritt__marke";
  marke.textContent = ZEICHEN[s.status] ?? "○";

  const mitte = document.createElement("div");
  mitte.className = "mp-schritt__mitte";
  const t = document.createElement("div");
  t.className = "mp-schritt__titel";
  t.textContent = `${s.kennung} · ${s.titel}`;
  mitte.appendChild(t);
  if (s.detail) {
    const d = document.createElement("div");
    d.className = "mp-schritt__detail";
    d.textContent = s.detail;
    mitte.appendChild(d);
  }
  if (s.notiz) {
    const n = document.createElement("div");
    n.className = "mp-schritt__detail";
    n.textContent = `Notiz: ${s.notiz}`;
    mitte.appendChild(n);
  }

  const rechts = document.createElement("div");
  rechts.className = "mp-schritt__rechts";
  if (s.hebel === 3) {
    const h = document.createElement("span");
    h.className = "mp-marker mp-marker--hebel";
    h.textContent = "wichtig";
    h.title = "blockiert anderes oder entscheidet über Geld";
    rechts.appendChild(h);
  }
  if (s.aufwand) {
    const a = document.createElement("span");
    a.className = "mp-marker";
    a.textContent = s.aufwand;
    a.title = AUFWAND_TEXT[s.aufwand] ?? "";
    rechts.appendChild(a);
  }

  row.append(marke, mitte, rechts);
  const klick = (): void => umschalten(s);
  row.addEventListener("click", klick);
  row.addEventListener("keydown", (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); klick(); } });
  return row;
}

export function initAwinTab(modal: HTMLElement, deps: MarketingDeps): void {
  const panel = modal.querySelector<HTMLElement>('[data-tab-panel="awin"]');
  if (!panel) return;
  const host = panel.querySelector<HTMLElement>("#awin-inhalt");
  if (!host) return;

  async function laden(): Promise<void> {
    host!.textContent = "Lade Awin-Checkliste …";
    const r = await deps.api<AwinAntwort>("/api/marketing/awin");
    if (r.status !== 200 || !r.data) {
      host!.textContent = "Checkliste konnte nicht geladen werden.";
      return;
    }
    const d = r.data;
    host!.textContent = "";
    if (!d.schritte.length) {
      host!.textContent = "Noch keine Schritte hinterlegt — awin_seed.mjs ausführen.";
      return;
    }
    host!.appendChild(balken(d.summe.erledigt, d.summe.gesamt));

    // Solange Phase A offen ist, steht der Hinweis oben. Danach verschwindet er von selbst.
    const aOffen = d.schritte.filter((x) => x.phase === "A" && x.status !== "erledigt" && x.status !== "verworfen");
    if (aOffen.length) {
      const warn = document.createElement("p");
      warn.className = "modal-desc";
      warn.textContent = `Phase A blockiert: ${aOffen.length} von 4 Antworten fehlen noch `
        + "(Awin, Steuerberater, Frank, Vertrag). Vorher lohnt keine Werbemittel-Produktion.";
      host!.appendChild(warn);
    }

    const umschalten = async (s: Schritt): Promise<void> => {
      const neu: Schritt["status"] = NAECHSTER[s.status] ?? "laeuft";
      const antwort = await deps.api("/api/marketing/awin/status", "POST", { kennung: s.kennung, status: neu });
      if (antwort.status === 200) await laden();
    };

    let aktuelle = "";
    let block: HTMLElement | null = null;
    for (const s of d.schritte) {
      if (s.phase !== aktuelle) {
        aktuelle = s.phase;
        const gruppe = d.schritte.filter((x) => x.phase === s.phase);
        const fertig = gruppe.filter((x) => x.status === "erledigt").length;
        const kopf = document.createElement("h4");
        kopf.className = "mp-saeule";
        kopf.textContent = `${s.phase} · ${s.phase_titel}`;
        const zahl = document.createElement("span");
        const komplett = fertig === gruppe.length;
        zahl.className = komplett ? "mp-saeule__zahl mp-saeule__zahl--fertig" : "mp-saeule__zahl";
        zahl.textContent = komplett ? `${fertig}/${gruppe.length} ✓` : `${fertig}/${gruppe.length}`;
        kopf.appendChild(zahl);
        block = document.createElement("div");
        block.className = "mp-block";
        host!.append(kopf, block);
      }
      block?.appendChild(zeile(s, (x) => { void umschalten(x); }));
    }
  }

  const btn = modal.querySelector<HTMLButtonElement>('[data-tab="awin"]');
  let geladen = false;
  btn?.addEventListener("click", () => { if (!geladen) { geladen = true; void laden(); } });
  panel.querySelector<HTMLButtonElement>("#awin-neu-laden")?.addEventListener("click", () => { void laden(); });
}
